/**
 * Date of birth, Gregorian (user-management §2.1, §2.8; the app is English
 * only since 2026-10-29). The API gets YYYY-MM-DD.
 */
import { formatIsoDate } from '@neosocial/protocol';
import { useMemo } from 'react';
import { useT } from '../i18n';
import { UI_LOCALE } from '../i18n/core';
import { Select } from '../ui/kit';

export interface BirthValue {
  year: number | null;
  month: number | null;
  day: number | null;
}

/** The ISO date for a picked value, or null when incomplete or not a real date. */
export function toIso(v: BirthValue): string | null {
  if (!v.year || !v.month || !v.day) return null;
  const t = new Date(Date.UTC(v.year, v.month - 1, v.day));
  if (t.getUTCMonth() !== v.month - 1 || t.getUTCDate() !== v.day) return null;
  return formatIsoDate({ year: v.year, month: v.month, day: v.day });
}

/**
 * How many days the picked month has (31 while the month is not picked;
 * February counts a leap year while the year is not picked).
 */
export function daysInMonth(v: Pick<BirthValue, 'year' | 'month'>): number {
  if (!v.month) return 31;
  return new Date(Date.UTC(v.year ?? 2000, v.month, 0)).getUTCDate();
}

/** A change of month or year: a day the new month doesn't have is cleared, never kept hidden. */
export function withMonthYear(v: BirthValue, next: Partial<BirthValue>): BirthValue {
  const out = { ...v, ...next };
  if (out.day && out.day > daysInMonth(out)) out.day = null;
  return out;
}

function monthNames(): string[] {
  const f = new Intl.DateTimeFormat(UI_LOCALE, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => f.format(new Date(Date.UTC(2001, i, 15))));
}

export function BirthDate({
  value,
  onChange,
  invalid,
}: {
  value: BirthValue;
  onChange(v: BirthValue): void;
  invalid?: boolean;
}) {
  const { t } = useT();
  const thisYear = new Date().getUTCFullYear();
  const years = useMemo(() => Array.from({ length: 100 }, (_, i) => thisYear - i), [thisYear]);
  const months = useMemo(() => monthNames(), []);
  const maxDay = daysInMonth(value);
  return (
    <div className="ns-birth">
      <div className="ns-birth-row">
        <Select
          className="nx-input ns-select"
          aria-label={t('ns.birth.day')}
          aria-invalid={invalid || undefined}
          value={value.day ?? ''}
          onChange={(e) =>
            onChange({ ...value, day: e.target.value ? Number(e.target.value) : null })
          }
        >
          <option value="">{t('ns.birth.day')}</option>
          {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>
        <Select
          className="nx-input ns-select"
          aria-label={t('ns.birth.month')}
          aria-invalid={invalid || undefined}
          value={value.month ?? ''}
          onChange={(e) =>
            onChange(
              withMonthYear(value, { month: e.target.value ? Number(e.target.value) : null }),
            )
          }
        >
          <option value="">{t('ns.birth.month')}</option>
          {months.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
        <Select
          className="nx-input ns-select"
          aria-label={t('ns.birth.year')}
          aria-invalid={invalid || undefined}
          value={value.year ?? ''}
          onChange={(e) =>
            onChange(withMonthYear(value, { year: e.target.value ? Number(e.target.value) : null }))
          }
        >
          <option value="">{t('ns.birth.year')}</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}
